import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Keys used for the mobile-browser fallback token vault. Mobile Safari ITP,
// Chrome incognito, Brave, and various proxy-based Lite browsers silently
// drop `SameSite=None; Secure` cookies even when the origins match. For those
// cases we also stash the JWTs in localStorage and attach them as Bearer
// headers. Cookies remain the primary transport on desktop.
const ACCESS_TOKEN_KEY = 'et.auth.access';
const REFRESH_TOKEN_KEY = 'et.auth.refresh';

// Triple-tier token vault: localStorage → sessionStorage → in-memory.
// Chrome Incognito and iOS Safari Private Mode partition (or disable)
// localStorage inside cross-origin iframes used by some preview embeds,
// silently throwing on writes. sessionStorage has the same partitioning but
// survives hard-reload within the same tab; in-memory is the final fallback
// so a user who just logged in never loses state within the current SPA
// session even when both Web Storage APIs are locked down.
const _memTokens = {};
const _safeStorage = (storage) => {
  try {
    const k = '__et_probe__';
    storage.setItem(k, '1');
    storage.removeItem(k);
    return storage;
  } catch (_) { return null; }
};
const _ls = typeof window !== 'undefined' ? _safeStorage(window.localStorage) : null;
const _ss = typeof window !== 'undefined' ? _safeStorage(window.sessionStorage) : null;

const readToken = (k) => {
  try { if (_ls) { const v = _ls.getItem(k); if (v) return v; } } catch (_) { /* */ }
  try { if (_ss) { const v = _ss.getItem(k); if (v) return v; } } catch (_) { /* */ }
  return _memTokens[k] || '';
};
const writeToken = (k, v) => {
  _memTokens[k] = v || '';
  try { if (_ls) { if (v) _ls.setItem(k, v); else _ls.removeItem(k); } } catch (_) { /* */ }
  try { if (_ss) { if (v) _ss.setItem(k, v); else _ss.removeItem(k); } } catch (_) { /* */ }
};

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

// ---------------------------------------------------------------------------
// Global Bearer-header interceptor
// ---------------------------------------------------------------------------
// Installed once at module load. For every axios request going to our API,
// attach `Authorization: Bearer <token>` if we have one stashed. This is
// idempotent — if the cookie is also present the backend prefers it, and
// otherwise the Bearer header takes over. Same applies to `/auth/refresh`
// which now also accepts `X-Refresh-Token` for the same reason.
axios.interceptors.request.use((config) => {
  const url = config.url || '';
  if (url.startsWith(API_URL) || url.startsWith('/api/')) {
    const access = readToken(ACCESS_TOKEN_KEY);
    if (access && !config.headers?.Authorization) {
      config.headers = { ...(config.headers || {}), Authorization: `Bearer ${access}` };
    }
    // Send the refresh token as an explicit header on /auth/refresh — used
    // only when the browser has already dropped the refresh_token cookie.
    if (url.includes('/auth/refresh')) {
      const refresh = readToken(REFRESH_TOKEN_KEY);
      if (refresh && !config.headers?.['X-Refresh-Token']) {
        config.headers = { ...(config.headers || {}), 'X-Refresh-Token': refresh };
      }
    }
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Global "Renew subscription" dialog state. Lives on the auth context
  // so every page + the global axios 402 interceptor can pop it up
  // through a single hook (`useAuth().showRenewDialog()`).
  const [renewDialog, setRenewDialog] = useState({ open: false, reason: '' });
  const showRenewDialog = useCallback((reason = '') => {
    // No-op if it's already open with the same reason — avoids
    // unnecessary re-renders from the 5-min idle interval firing while
    // the dialog is still mounted.
    setRenewDialog((prev) => (prev.open ? prev : { open: true, reason }));
  }, []);
  const closeRenewDialog = useCallback(() => {
    setRenewDialog((prev) => ({ ...prev, open: false }));
  }, []);

  // Derived flag — true when the workspace owner's trial AND subscription
  // have both elapsed. Sub-users inherit this from the admin (the backend
  // already swaps to admin doc for sub-user requests, so `access_reason`
  // here reflects the workspace state). Read-only mode is keyed off this.
  const isExpired = user && user !== false && (
    user.access_reason === 'expired' || user.has_access === false
  );

  useEffect(() => {
    checkAuth();
  }, []);

  // Silent session-refresh interceptor — retries any 401 once via /auth/refresh.
  // Also auto-pops the global Renew dialog when ANY API call returns 402 with
  // code=SUBSCRIPTION_EXPIRED. This is the belt-and-braces guard if a write
  // slipped past the per-button UI check.
  useEffect(() => {
    let refreshInFlight = null;

    const interceptor = axios.interceptors.response.use(
      (resp) => resp,
      async (error) => {
        const original = error.config;
        const status = error.response?.status;
        // Global 402 → trigger renew dialog (no need to retry).
        // Sub-user-of-expired-admin gets SUB_USER_LOCKED_OUT — handled
        // by the `SubUserLockout` interstitial in PaywallGuard, NOT
        // this dialog. We skip the popup for that code so the sub-user
        // sees a clean lockout screen instead of layered modals.
        if (
          status === 402
          && error.response?.data?.code === 'SUBSCRIPTION_EXPIRED'
        ) {
          try {
            setRenewDialog({
              open: true,
              reason: error.response?.data?.detail || 'Your subscription has expired.',
            });
          } catch (_) { /* */ }
          return Promise.reject(error);
        }
        if (
          status !== 401
          || !original
          || original._retriedAfterRefresh
          || (original.url || '').includes('/auth/refresh')
          || (original.url || '').includes('/auth/logout')
          || (original.url || '').includes('/auth/login')
          || (original.url || '').includes('/auth/register')
        ) {
          return Promise.reject(error);
        }

        try {
          if (!refreshInFlight) {
            refreshInFlight = axios
              .post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true })
              .then((r) => {
                // Persist the refreshed access token for subsequent Bearer use.
                const newAccess = r.data?.access_token;
                if (newAccess) writeToken(ACCESS_TOKEN_KEY, newAccess);
                return r;
              })
              .finally(() => { refreshInFlight = null; });
          }
          await refreshInFlight;
        } catch (_) {
          setUser(false);
          writeToken(ACCESS_TOKEN_KEY, '');
          writeToken(REFRESH_TOKEN_KEY, '');
          return Promise.reject(error);
        }

        original._retriedAfterRefresh = true;
        return axios(original);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (_) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  // On successful auth, persist both JWTs in localStorage so subsequent
  // requests on mobile (where cookies may have been dropped) can use the
  // Authorization header instead.
  const persistTokens = (data) => {
    if (data?.access_token) writeToken(ACCESS_TOKEN_KEY, data.access_token);
    if (data?.refresh_token) writeToken(REFRESH_TOKEN_KEY, data.refresh_token);
  };

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true },
      );
      persistTokens(data);
      setUser(data);
      return { success: true };
    } catch (error) {
      // iter-217 — storage overage hard block returns 402 with a dict detail.
      // Surface it so Login.js can route the user to the overage pay page.
      const detail = error.response?.data?.detail;
      if (error.response?.status === 402 && detail?.code === 'STORAGE_BLOCK_ACTIVE') {
        return {
          success: false,
          storageBlock: true,
          error: detail.message || 'Storage overage blocking login',
          email: detail.email || email,
        };
      }
      // iter-233 — unverified account. Login.js routes to /verify-email.
      if (error.response?.status === 403 && detail?.code === 'EMAIL_NOT_VERIFIED') {
        return {
          success: false,
          emailNotVerified: true,
          error: detail.message || 'Please verify your email first.',
          email: detail.email || email,
        };
      }
      // iter-234e — paused by admin. Login.js shows the Paused screen
      // directly on the login page instead of going into the app.
      if (error.response?.status === 403 && detail?.code === 'SUBSCRIPTION_PAUSED') {
        return {
          success: false,
          subscriptionPaused: true,
          error: detail.message || 'Your access has been paused.',
          email: detail.email || email,
          pauseReason: detail.pause_reason || '',
        };
      }
      return {
        success: false,
        error: formatApiErrorDetail(detail) || error.message,
      };
    }
  };

  const register = async (email, password, name, pan, dateOfBirth, mobileNumber) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        { email, password, name, pan, date_of_birth: dateOfBirth, mobile_number: mobileNumber },
        { withCredentials: true },
      );
      // Iter-233 — trial-abuse OTP flow. Backend no longer issues
      // session tokens on register; it returns `pending_verification:true`
      // and the caller routes the user to the Verify Email screen.
      if (data?.pending_verification) {
        return { success: true, pendingVerification: true, email: data.email };
      }
      persistTokens(data);
      setUser(data);
      return { success: true };
    } catch (error) {
      const noResponse = !error.response;
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (noResponse || (status && status >= 500)) {
        try {
          const loginResp = await axios.post(
            `${API_URL}/api/auth/login`,
            { email, password },
            { withCredentials: true },
          );
          persistTokens(loginResp.data);
          setUser(loginResp.data);
          return { success: true };
        } catch (_loginErr) {
          return {
            success: false,
            error:
              "We couldn't confirm your registration (network hiccup). If you see this more than once, please try signing in directly on the Login page — your account may already exist.",
          };
        }
      }
      // Structured abuse-guard errors from the backend surface as
      // `{code, message}` dicts; prefer the human-readable `message`
      // when available so Register.js shows the exact reason (disposable
      // domain, PAN re-use, alias in use, etc).
      if (detail && typeof detail === 'object' && detail.code) {
        return { success: false, code: detail.code, error: detail.message || 'Signup blocked.' };
      }
      return {
        success: false,
        error: formatApiErrorDetail(detail) || error.message,
      };
    }
  };

  const verifyEmail = async (email, otp) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/verify-email`,
        { email, otp },
        { withCredentials: true },
      );
      persistTokens(data);
      setUser(data);
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code) {
        return { success: false, code: detail.code, error: detail.message };
      }
      return { success: false, error: formatApiErrorDetail(detail) || error.message };
    }
  };

  const resendOtp = async (email) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/resend-otp`,
        { email },
        { withCredentials: true },
      );
      return { success: true, message: data?.message || 'A new code has been sent.' };
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code) {
        return { success: false, code: detail.code, error: detail.message };
      }
      return { success: false, error: formatApiErrorDetail(detail) || error.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (_) { /* swallow — we still clear local state below */ }
    writeToken(ACCESS_TOKEN_KEY, '');
    writeToken(REFRESH_TOKEN_KEY, '');
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      verifyEmail,
      resendOtp,
      logout,
      refreshUser: checkAuth,
      isExpired,
      renewDialog,
      showRenewDialog,
      closeRenewDialog,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
